import React, { useState, useMemo } from 'react';
import { useApp } from '../store';
import { Project, ProjectStage, UserRole } from '../types';
// Fix: remove parseISO as it is not available from the installed date-fns version
import { format } from 'date-fns';
import { Search, Archive, ArrowRight, User as UserIcon, Calendar, CheckCircle, MoreVertical } from 'lucide-react';
import { ProjectDetailModal } from '../components/ProjectDetailModal';

const CompletedProjects = () => {
  const { projects, users, currentUser } = useApp();
  const [search, setSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const completedProjects = useMemo(() => {
    let list = projects.filter(p => p.stage === ProjectStage.COMPLETED);
    
    // Non-admins only see their own completed work
    if (currentUser?.role !== UserRole.ADMIN) {
      list = list.filter(p => 
        p.assignedDesignerId === currentUser?.id || 
        p.assignedDevManagerId === currentUser?.id || 
        p.assignedQAId === currentUser?.id
      );
    }

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.clientName.toLowerCase().includes(q));
    }

    return list.sort((a, b) => {
        // Fix: Replace parseISO with native Date constructor
        const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return dateB - dateA; // Newest first
    });
  }, [projects, search, currentUser]);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight uppercase">Completed Projects</h2>
          <p className="text-slate-500 font-bold mt-1 uppercase tracking-widest text-xs">Closed & Successfully Delivered Projects</p>
        </div>

        <div className="relative w-full max-w-sm">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search completed projects..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-[2.5rem] overflow-hidden shadow-sm">
        {completedProjects.length === 0 ? (
          <div className="p-24 text-center">
            <Archive className="w-16 h-16 text-slate-100 mx-auto mb-6" />
            <h4 className="text-xl font-black text-slate-900 mb-2 uppercase tracking-tight">List Empty</h4>
            <p className="text-slate-400 font-medium">No projects have been marked as completed yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Client & Title</th>
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Production Leads</th>
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Delivery Date</th>
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Records</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {completedProjects.map(project => {
                  const designer = users.find(u => u.id === project.assignedDesignerId);
                  const dev = users.find(u => u.id === project.assignedDevManagerId);
                  
                  return (
                    <tr key={project.id} className="hover:bg-slate-50 transition-colors group">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                           <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 shadow-inner">
                             <CheckCircle className="w-5 h-5" />
                           </div>
                           <div>
                             <p className="font-black text-slate-900 text-lg tracking-tight group-hover:text-indigo-600 transition-colors">{project.name}</p>
                             <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-0.5">{project.clientName}</p>
                           </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-3">
                           <div className="flex -space-x-2">
                             {[designer, dev].filter(Boolean).map(lead => (
                               <img key={lead?.id} src={lead?.avatar} className="w-7 h-7 rounded-full border-2 border-white shadow-sm" alt="" title={lead?.name} />
                             ))}
                           </div>
                           <div className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">
                             {designer?.name.split(' ')[0]} & {dev?.name.split(' ')[0]}
                           </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-slate-300" />
                          <span className="text-sm font-black text-slate-700">
                            {/* Fix: Replace parseISO with native Date constructor */}
                            {project.completedAt ? format(new Date(project.completedAt), 'MMM d, yyyy') : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-8 py-6 text-center">
                         <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                           Finalized
                         </span>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <button 
                          onClick={() => setSelectedProject(project)}
                          className="p-3 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-2xl transition-all shadow-sm hover:shadow-indigo-100 border border-transparent hover:border-indigo-100"
                        >
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedProject && (
        <ProjectDetailModal 
          project={selectedProject} 
          onClose={() => setSelectedProject(null)} 
        />
      )}
    </div>
  );
};

export default CompletedProjects;